import { Box, Text } from '@chakra-ui/react';
import { parsePlanMarkdown, SafeMarkdown } from '../safeMarkdown';
import { planHasDetails, type MarketingPlansView, type PlanView } from './model';

export { parsePlanMarkdown };

function PlanMarkdown({ text }: { text: string }) {
  return (
    <SafeMarkdown
      text={text}
      testId="marketing-plan-markdown"
      tableTestId="marketing-plan-markdown-table"
      fontSize="12px"
      color="gray.700"
      mt="4px"
    />
  );
}

function MetaLine({ plan }: { plan: PlanView }) {
  const parts: string[] = [];
  if (plan.revision) parts.push(`Revision ${plan.revision}`);
  if (plan.weeklyBudget) parts.push(`Weekly budget ${plan.weeklyBudget}`);
  if (plan.phase2Budget) parts.push(`Phase 2 ${plan.phase2Budget}`);
  if (plan.margin) parts.push(`Margin ${plan.margin}`);
  if (!parts.length) return null;
  return <Text fontSize="12px" color="gray.700">{parts.join(' · ')}</Text>;
}

function TestTable({ plan }: { plan: PlanView }) {
  if (!plan.tests.length) return null;
  const showBudget = plan.tests.some((test) => test.budget);
  const showBlockers = plan.tests.some((test) => test.blockers.length > 0);
  return (
    <Box as="table" w="100%" fontSize="12px" mt="8px" data-testid="marketing-plan-tests" sx={{ borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <Text as="th" textAlign="left" py="2px" fontWeight="600" color="gray.500">Test</Text>
          {showBudget ? <Text as="th" textAlign="right" py="2px" fontWeight="600" color="gray.500">Budget</Text> : null}
          {showBlockers ? <Text as="th" textAlign="left" py="2px" pl="12px" fontWeight="600" color="gray.500">Blockers</Text> : null}
        </tr>
      </thead>
      <tbody>
        {plan.tests.map((test, index) => (
          <tr key={`${test.label}-${index}`}>
            <Text as="td" py="2px" fontWeight="600">{test.label}</Text>
            {showBudget ? <Text as="td" py="2px" textAlign="right" fontFamily="mono">{test.budget ?? '\u2014'}</Text> : null}
            {showBlockers ? (
              <Text as="td" py="2px" pl="12px" color={test.blockers.length ? 'orange.700' : 'gray.500'}>
                {test.blockers.length ? test.blockers.join('; ') : '\u2014'}
              </Text>
            ) : null}
          </tr>
        ))}
      </tbody>
    </Box>
  );
}

function BlockerList({ blockers }: { blockers: string[] }) {
  if (!blockers.length) return null;
  return (
    <Box mt="6px" data-testid="marketing-plan-blockers">
      {blockers.map((blocker, index) => (
        <Text key={`${blocker}-${index}`} fontSize="12px" color="orange.700">{blocker}</Text>
      ))}
    </Box>
  );
}

function PlanSection({ plan, showTitle }: { plan: PlanView; showTitle: boolean }) {
  return (
    <Box data-testid="marketing-plan-item" pt="8px">
      {showTitle ? (
        <Box display="flex" gap="8px" alignItems="baseline" flexWrap="wrap">
          <Text fontSize="13px" fontWeight="700">{plan.title}</Text>
          {plan.status ? <Text fontSize="12px" color="gray.600">{plan.status}</Text> : null}
        </Box>
      ) : null}
      <MetaLine plan={plan} />
      {plan.counts ? <Text fontSize="11px" color="gray.600" data-testid="marketing-plan-counts">{plan.counts}</Text> : null}
      {plan.summary ? <Text fontSize="12px" color="gray.700" whiteSpace="pre-wrap" mt="4px">{plan.summary}</Text> : null}
      {plan.markdown ? <PlanMarkdown text={plan.markdown} /> : null}
      <BlockerList blockers={plan.blockers} />
      <TestTable plan={plan} />
      {!planHasDetails(plan) ? <Text fontSize="12px" color="gray.500">No plan details in this result.</Text> : null}
    </Box>
  );
}

function listCounts(view: MarketingPlansView): string | null {
  if (!view.counts) return null;
  const planCount = view.plans.length === 1
    ? '1 plan'
    : `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(view.plans.length)} plans`;
  const parts = view.counts.split(' · ').filter((part) => part !== planCount);
  return parts.length ? parts.join(' · ') : null;
}

/** Read-only plan summary. The shell owns the card title. No actions. */
export function MarketingPlanBody({ view }: { view: MarketingPlansView }) {
  const showTitles = view.kind === 'marketingPlanList' || view.plans.length > 1;
  const counts = listCounts(view);
  return (
    <Box>
      {counts ? <Text fontSize="11px" color="gray.600" data-testid="marketing-plan-counts">{counts}</Text> : null}
      {view.summary ? <Text fontSize="12px" color="gray.700" whiteSpace="pre-wrap" mt="4px">{view.summary}</Text> : null}
      {view.markdown ? <PlanMarkdown text={view.markdown} /> : null}
      <BlockerList blockers={view.blockers} />
      {view.plans.length === 0 && !view.summary && !view.counts && !view.markdown && view.blockers.length === 0 ? (
        <Text fontSize="12px" color="gray.500" mt="6px">No plan details in this result.</Text>
      ) : null}
      {view.plans.map((plan, index) => (
        <PlanSection key={`${plan.title}-${index}`} plan={plan} showTitle={showTitles} />
      ))}
    </Box>
  );
}
